import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PROTOCOL_NODES } from "@/lib/demo-data";
import { cn } from "@/lib/utils";
import { MonoLabel } from "./primitives";

const NODES = [
  { x: 80, y: 292 },
  { x: 218, y: 132 },
  { x: 368, y: 252 },
  { x: 518, y: 104 },
  { x: 664, y: 228 },
  { x: 806, y: 112 },
  { x: 928, y: 276 },
];

const LINKS = NODES.slice(0, -1).map((n, i) => {
  const a = n;
  const b = NODES[i + 1];
  const mx = (a.x + b.x) / 2;
  return `M ${a.x} ${a.y} Q ${mx} ${Math.min(a.y, b.y) - 70} ${b.x} ${b.y}`;
});

/** Large interactive architecture diagram — nodes + animated data flows. */
export function ProtocolGraph() {
  const [active, setActive] = useState(4);
  const reduced = useReducedMotion();
  const node = PROTOCOL_NODES[active];

  return (
    <div data-testid="protocol-graph">
      {/* Desktop diagram */}
      <div className="panel scanlines hud-corners hidden p-6 md:block">
        <svg viewBox="0 0 1000 380" className="h-auto w-full" role="img" aria-label="Consequence Protocol architecture diagram">
          <defs>
            <linearGradient id="flowGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#0066FF" />
              <stop offset="100%" stopColor="#00E5FF" />
            </linearGradient>
          </defs>

          {LINKS.map((d, i) => (
            <g key={i}>
              <path d={d} fill="none" stroke="rgba(120,170,255,0.18)" strokeWidth="1" />
              {i === active - 1 || i === active ? (
                <path d={d} fill="none" stroke="url(#flowGrad)" strokeWidth="1.5" className="anim-flow" />
              ) : null}
              {!reduced && (
                <circle r="3" fill="#00E5FF">
                  <animateMotion dur={`${2.4 + i * 0.3}s`} repeatCount="indefinite" path={d} />
                </circle>
              )}
            </g>
          ))}

          {NODES.map((n, i) => {
            const isActive = i === active;
            return (
              <g
                key={i}
                onClick={() => setActive(i)}
                className="cursor-pointer"
                data-testid={`protocol-node-${PROTOCOL_NODES[i].name.toLowerCase()}`}
              >
                {isActive && <circle cx={n.x} cy={n.y} r="46" fill="rgba(0,229,255,0.06)" />}
                <circle
                  cx={n.x}
                  cy={n.y}
                  r="34"
                  fill={isActive ? "#071A35" : "#050912"}
                  stroke={isActive ? "#00E5FF" : "rgba(120,170,255,0.3)"}
                  strokeWidth={isActive ? 1.5 : 1}
                />
                <text
                  x={n.x}
                  y={n.y + 4}
                  textAnchor="middle"
                  className="fill-current font-mono"
                  fontSize="12"
                  fill={isActive ? "#00E5FF" : "#8190A8"}
                >
                  {String(i + 1).padStart(2, "0")}
                </text>
                <text
                  x={n.x}
                  y={n.y + 58}
                  textAnchor="middle"
                  fontSize="11"
                  letterSpacing="2"
                  className="font-mono uppercase"
                  fill={isActive ? "#F5F9FF" : "#8190A8"}
                >
                  {PROTOCOL_NODES[i].name}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Mobile fallback */}
      <div className="panel hud-corners md:hidden">
        {PROTOCOL_NODES.map((s, i) => (
          <button
            key={s.id}
            data-testid={`protocol-node-mobile-${s.name.toLowerCase()}`}
            onClick={() => setActive(i)}
            className={cn(
              "flex w-full items-center gap-4 border-b border-line/60 p-4 text-left transition-colors duration-200",
              i === active ? "bg-cyan/5" : ""
            )}
          >
            <span className={cn("font-mono text-xs", i === active ? "text-cyan" : "text-dim")}>{s.id}</span>
            <span className={cn("font-mono text-xs uppercase tracking-[0.18em]", i === active ? "text-ink" : "text-dim")}>
              {s.name}
            </span>
          </button>
        ))}
      </div>

      <motion.div
        key={node.id}
        initial={reduced ? undefined : { opacity: 0, y: 10 }}
        animate={reduced ? undefined : { opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="panel scanlines hud-corners mt-6 p-6 sm:p-8"
        data-testid="protocol-node-detail"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-cyan">
            {node.id} — {node.name}
          </h3>
          <MonoLabel className="text-[9px]">{node.meta}</MonoLabel>
        </div>
        <p className="mt-4 font-display text-lg text-ink sm:text-xl">{node.desc}</p>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-dim">{node.detail}</p>
      </motion.div>
    </div>
  );
}
